import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Aviso } from './aviso';
import { Button } from './button';

/**
 * Un vacío que dice por qué está vacío (mapa de diseño 226, hallazgo 8): borde
 * punteado, el título y una frase. Reemplaza las tres formas que había.
 */
export function EstadoVacio({
  titulo,
  children,
  icono: Icono,
  accion,
  className,
}: {
  titulo: ReactNode;
  children?: ReactNode;
  icono?: LucideIcon;
  accion?: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-testid="estado-vacio"
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-lg border border-dashed px-5 py-6 text-center text-sm text-muted-foreground',
        className
      )}
    >
      {Icono && <Icono aria-hidden="true" className="h-6 w-6" />}
      <p className="text-sm font-semibold text-foreground">{titulo}</p>
      {children && <div className="max-w-md">{children}</div>}
      {accion && <div className="mt-1">{accion}</div>}
    </div>
  );
}

/**
 * Un error que se puede reintentar: el aviso en tono de bloqueo y el botón.
 * Reemplaza las cinco copias sueltas. Un error nunca se ve como «todo al día».
 */
export function EstadoError({
  children,
  onReintentar,
  textoReintentar = 'Reintentar',
  className,
}: {
  children: ReactNode;
  onReintentar?: () => void;
  textoReintentar?: string;
  className?: string;
}) {
  return (
    <Aviso tono="bloqueo" role="alert" className={cn('text-sm', className)}>
      <div className="flex flex-wrap items-center gap-3">
        <span>{children}</span>
        {onReintentar && (
          <Button size="sm" variant="outline" onClick={onReintentar}>
            {textoReintentar}
          </Button>
        )}
      </div>
    </Aviso>
  );
}
